import { useState, type ReactNode } from "react";
import type { DeleteTarget } from "./deleteTarget";
import { EntityDeleteModal } from "./EntityDeleteModal";
import { FormDrawer } from "./FormDrawer";
import type { Errors } from "./useDraft";
import { useEntityForm, type Convert } from "./useEntityForm";
import type { SaveOptions } from "./useSave";

/** 表单字段组件拿到的东西：草稿、字段错误与按字段更新。 */
export interface EntityFormState<D> { draft: D; errors: Errors<D>; set: <K extends keyof D>(k: K, v: D[K]) => void }

export interface EntityDrawerProps<D extends object, In, Out> extends Omit<SaveOptions<Out>, "onSaved"> {
  title: ReactNode;
  /** 提交按钮的文案，缺省"保存"。 */
  submitLabel?: string;
  initial: D;
  convert: Convert<D, In>;
  save: (body: In) => Promise<Out>;
  onClose: () => void;
  /** 表单字段，按当前草稿渲染。 */
  children: (form: EntityFormState<D>) => ReactNode;
  /** 编辑已有实体时给出，底栏多一个“删除”；删除成功后抽屉随之关闭（D180）。 */
  deletion?: DeleteTarget;
}

/** 主数据的新建与编辑抽屉：草稿、换算、保存、关闭一套组装好，页面只提供草稿的初值、换算与保存函数（issue #29）。 */
export function EntityDrawer<D extends object, In, Out>({ title, submitLabel, initial, convert, save, invalidate, message, onClose, children, deletion }: EntityDrawerProps<D, In, Out>) {
  const form = useEntityForm(initial, convert, save, { invalidate, message, onSaved: onClose });
  const [deleting, setDeleting] = useState(false);
  return (
    <>
      <FormDrawer open title={title} submitLabel={submitLabel} saving={form.saving} error={form.error} warning={form.warning} onSubmit={form.submit} onClose={onClose}
        onDelete={deletion && (() => setDeleting(true))}>
        {children({ draft: form.draft, errors: form.errors, set: form.set })}
      </FormDrawer>
      {deleting && deletion && <EntityDeleteModal target={deletion} onClose={() => setDeleting(false)} onDeleted={() => { onClose(); deletion.after?.(); }} />}
    </>
  );
}
